import { ENGINE_ABI_VERSION, } from "./types.js";
import { setActiveClassifier } from "./dsp.js";
export class EngineClient {
    constructor(node) {
        Object.defineProperty(this, "node", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: node
        });
        Object.defineProperty(this, "usingWasm", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "listeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "ready", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.node.port.onmessage = (e) => this.onMessage(e.data);
    }
    async init(wasmModule, wasmFactory, config, glueUrl, wasmUrl, wasmBytes) {
        console.info(`[EngineClient] init abi=${ENGINE_ABI_VERSION} wasm=${!!wasmModule} ` +
            `glue=${!!glueUrl}`);
        const initStart = performance.now();
        const initErrors = [];
        return new Promise((resolve, reject) => {
            let settled = false;
            const to = setTimeout(() => {
                console.error("[EngineClient] init timeout after 15000ms — " +
                    "processor may not have been instantiated. " +
                    (initErrors.length
                        ? "worklet errors seen:\n  " + initErrors.join("\n  ")
                        : "no worklet 'error' messages were received"));
                reject(new Error("engine init timeout"));
            }, 15000);
            const onReady = (events) => void events;
            void onReady;
            const handler = (e) => {
                const msg = e.data;
                if (msg.type === "error") {
                    initErrors.push(msg.message);
                    console.error("[engine:init]", msg.message);
                    return;
                }
                if (msg.type === "ready") {
                    if (settled)
                        return;
                    if (msg.abi !== ENGINE_ABI_VERSION) {
                        clearTimeout(to);
                        console.error(`[EngineClient] ABI mismatch expected=${ENGINE_ABI_VERSION} ` +
                            `got=${msg.abi}`);
                        reject(new Error(`engine ABI mismatch: expected ${ENGINE_ABI_VERSION}, got ${msg.abi}`));
                        return;
                    }
                    settled = true;
                    this.ready = true;
                    this.usingWasm = msg.usingWasm;
                    clearTimeout(to);
                    const elapsed = (performance.now() - initStart).toFixed(0);
                    console.info(`[EngineClient] ready usingWasm=${msg.usingWasm} ` +
                        `(handshake ${elapsed}ms)`);
                    if ((wasmFactory || glueUrl) && !msg.usingWasm) {
                        console.info("[EngineClient] a compiled WASM module was supplied " +
                            "but the worklet chose the JS fallback.");
                        console.warn("[EngineClient] WASM NOT LOADED despite a glue URL " +
                            "being supplied: the worklet failed to " +
                            "self-instantiate. Running JS DSP fallback.\n" +
                            "  fallbackReason=" +
                            (msg.fallbackReason || "(not reported)") +
                            "\n" +
                            (initErrors.length
                                ? "  worklet errors during init:\n    " +
                                    initErrors.join("\n    ")
                                : "  (no worklet 'error' messages received — " +
                                    "the failure happened before any error was " +
                                    "posted, or errors were dropped)"));
                    }
                    this.node.port.onmessage = (ev) => this.onMessage(ev.data);
                    resolve();
                }
            };
            this.node.port.onmessage = handler;
            const basePayload = {
                type: "init",
                abi: ENGINE_ABI_VERSION,
                wasmFactory: null,
                glueUrl: glueUrl ?? null,
                wasmUrl: wasmUrl ?? null,
                config,
            };
            try {
                this.post({ ...basePayload, wasmModule: null });
                console.info("[EngineClient] init posted (without module; wasm bytes " +
                    "follow in separate 'wasm-bytes' message=" +
                    !!wasmBytes +
                    ")");
            }
            catch (err) {
                clearTimeout(to);
                reject(new Error("engine init postMessage failed: " + String(err)));
                return;
            }
            if (wasmBytes) {
                try {
                    this.node.port.postMessage({ type: "wasm-bytes", wasmBytes }, [wasmBytes]);
                    console.info("[EngineClient] wasm-bytes posted (transferred) as a " +
                        "separate follow-up message");
                }
                catch (err) {
                    console.warn("[EngineClient] posting wasm-bytes failed; the " +
                        "worklet will fall back to JS DSP.", err);
                    this.post({ type: "wasm-bytes", wasmBytes: null });
                }
            }
            else {
                console.info("[EngineClient] no wasm bytes; posting null 'wasm-bytes' " +
                    "sentinel so the worklet proceeds with JS DSP fallback");
                this.post({
                    type: "wasm-bytes",
                    wasmBytes: null,
                });
            }
        });
    }
    setClusters(clusters) {
        console.debug(`[EngineClient] setClusters count=${clusters.length}`);
        if (clusters.length === 0) {
            console.warn("[EngineClient] setClusters called with empty array — " +
                "engine will emit only silence events");
        }
        this.post({ type: "set-clusters", clusters });
    }
    setClassifier(classifier) {
        console.debug(`[EngineClient] setClassifier ready=${!!classifier?.ready}`);
        setActiveClassifier(classifier ?? null);
        void this.postClassifierToWorklet(classifier);
    }
    setCalibrating(calibrating) {
        console.debug(`[EngineClient] setCalibrating=${calibrating}`);
        this.post({ type: "set-mode", calibrating });
    }
    setConfig(config) {
        console.debug("[EngineClient] setConfig", config);
        this.post({ type: "set-config", config });
    }
    reset() {
        console.debug("[EngineClient] reset");
        this.post({ type: "reset" });
    }
    onEvents(fn) {
        this.listeners.add(fn);
        return () => this.listeners.delete(fn);
    }
    isReady() {
        return this.ready;
    }
    async postClassifierToWorklet(classifier) {
        try {
            if (!classifier || !classifier.ready) {
                this.post({ type: "set-classifier", classifier: null });
                return;
            }
            const serialized = await classifier.serialize();
            this.post({ type: "set-classifier", classifier: serialized });
            console.info("[EngineClient] posted serialized classifier to worklet " +
                `(labels: ${serialized?.labels.join(",") ?? "none"})`);
        }
        catch (err) {
            console.warn("[EngineClient] failed to post classifier to worklet", err);
        }
    }
    post(msg) {
        this.node.port.postMessage(msg);
    }
    onMessage(msg) {
        if (msg.type === "events") {
            if (!this.ready) {
                console.warn("[EngineClient] received events before ready handshake");
            }
            for (const fn of this.listeners)
                fn(msg.events);
        }
        else if (msg.type === "error") {
            console.error("[engine]", msg.message);
        }
        else {
            console.warn("[EngineClient] unhandled message type", msg);
        }
    }
}
